// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { fileAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Upload, 
  FolderOpen, 
  File, 
  Image as ImageIcon,
  FileText,
  FileVideo,
  FileAudio,
  FileArchive,
  Trash2,
  Download,
  Loader2,
  Grid,
  List,
  Eye,
  Timer,
  CalendarPlus,
  Settings,
  Save,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatFileSize, getFileIcon } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:/, 'https:');

const getExpiryInfo = (expiresAt) => {
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 0) return { label: 'Expiré', color: '#ef4444', bg: 'rgba(239,68,68,0.1)', days: 0 };
  if (daysLeft <= 7) return { label: `${daysLeft}j`, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', days: daysLeft };
  if (daysLeft <= 30) return { label: `${daysLeft}j`, color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', days: daysLeft };
  return { label: `${daysLeft}j`, color: '#22c55e', bg: 'rgba(34,197,94,0.1)', days: daysLeft };
};

export default function FilesPage() {
  const { workspaceId } = useParams();
  const { canManage } = useAuth();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [storageType, setStorageType] = useState("local");
  const [previewFile, setPreviewFile] = useState(null);
  const [showRetentionSettings, setShowRetentionSettings] = useState(false);
  const [retentionConfig, setRetentionConfig] = useState(null);
  const [retentionForm, setRetentionForm] = useState({});
  const [savingRetention, setSavingRetention] = useState(false);
  const [editingExpiryId, setEditingExpiryId] = useState(null);
  const [editingExpiryDays, setEditingExpiryDays] = useState('');

  useEffect(() => {
    loadFiles();
  }, [workspaceId]);

  const loadFiles = async () => {
    try {
      const response = await fileAPI.list(workspaceId);
      setFiles(response.data);
    } catch (error) {
      console.error("Failed to load files:", error);
      toast.error("Erreur lors du chargement des fichiers");
    } finally {
      setLoading(false);
    }
  };

  const loadRetention = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/workspaces/${workspaceId}/files/retention`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setRetentionConfig(data);
        setRetentionForm(data.custom || {});
      }
    } catch (err) {
      console.error('Failed to load retention config:', err);
    }
  };

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/workspaces/${workspaceId}/files/retention`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(retentionForm),
        }
      );
      if (res.ok) {
        toast.success('Configuration de rétention sauvegardée');
        loadRetention();
      }
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingRetention(false);
    }
  };

  const handleUpload = async (e) => {
    const uploadFiles = e.target.files;
    if (!uploadFiles?.length) return;

    setUploading(true);

    try {
      for (const file of uploadFiles) {
        if (file.size > 100 * 1024 * 1024) {
          toast.error(`${file.name} est trop volumineux (max 100MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("storage_type", storageType);

        const response = await fileAPI.upload(workspaceId, formData);
        setFiles(prev => [...prev, response.data]);
      }
      toast.success("Fichier(s) téléversé(s) avec succès");
    } catch (error) {
      console.error("Failed to upload:", error);
      toast.error("Erreur lors du téléversement");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);

    try {
      await fileAPI.delete(workspaceId, deleteId);
      setFiles(files.filter(f => f.id !== deleteId));
      toast.success("Fichier supprimé");
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleExtend = async (fileId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/workspaces/${workspaceId}/files/${fileId}/expiry?days=30`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success("Expiration prolongée de 30 jours");
        loadFiles();
      }
    } catch (error) {
      toast.error("Erreur lors de la prolongation");
    }
  };

  const handleSetExpiry = async (fileId) => {
    const days = parseInt(editingExpiryDays);
    if (!days || days < 1) {
      toast.error("Veuillez entrer un nombre de jours valide");
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/workspaces/${workspaceId}/files/${fileId}/expiry?days=${days}`,
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success(`Expiration mise à ${days} jours`);
        setEditingExpiryId(null);
        setEditingExpiryDays('');
        loadFiles();
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getFileIconComponent = (fileType) => {
    const iconType = getFileIcon(fileType);
    const icons = {
      image: ImageIcon,
      video: FileVideo,
      audio: FileAudio,
      pdf: FileText,
      doc: FileText,
      xls: FileText,
      ppt: FileText,
      zip: FileArchive,
      file: File,
    };
    const IconComponent = icons[iconType] || File;
    return <IconComponent className="h-6 w-6" />;
  };

  const isPreviewable = (fileType) => {
    return fileType.startsWith('image/') || fileType === 'application/pdf';
  };

  const getFileUrl = (file) => {
    if (file.storage_type === "s3") {
      return file.url;
    }
    return `${BACKEND_URL}${file.url}`;
  };

  const handleDownload = async (file) => {
    try {
      const url = getFileUrl(file);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(getFileUrl(file), '_blank');
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="h-32 skeleton rounded-lg" />
            ))}
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Fichiers</h1>
            <p className="mt-1 text-muted-foreground">
              {files.length} fichier(s) dans cet espace
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CategoryManagerButton />
            {/* View Toggle */}
            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Storage Type */}
            <Select value={storageType} onValueChange={setStorageType}>
              <SelectTrigger className="w-32" data-testid="storage-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="s3">S3</SelectItem>
              </SelectContent>
            </Select>

            {/* Retention Settings Toggle (Trainers/Admins) */}
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => {
                  setShowRetentionSettings(!showRetentionSettings);
                  if (!retentionConfig) loadRetention();
                }}
              >
                <Settings className="h-4 w-4" />
                Rétention
                {showRetentionSettings ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            )}
            
            {/* Upload */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleUpload}
            />
            <Button 
              className="gap-2" 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-file-btn"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Téléverser
            </Button>
          </div>
        </div>

        {/* Retention Config Panel (Trainers/Admins) */}
        {canManage && showRetentionSettings && (
          <Card className="mt-4 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Settings className="h-4 w-4 text-primary" />
                  Configuration de la rétention des fichiers
                </h3>
                <Button size="sm" className="gap-1" onClick={saveRetention} disabled={savingRetention}>
                  {savingRetention ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Sauvegarder
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Définissez la durée de rétention (en jours) pour chaque type de fichier. Laissez vide pour garder la valeur par défaut.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {[
                  { key: 'image', label: '🖼️ Images', defaultVal: retentionConfig?.defaults?.image || 180 },
                  { key: 'video', label: '🎥 Vidéos', defaultVal: retentionConfig?.defaults?.video || 60 },
                  { key: 'audio', label: '🎵 Audio', defaultVal: retentionConfig?.defaults?.audio || 90 },
                  { key: 'application/pdf', label: '📄 PDF', defaultVal: retentionConfig?.defaults?.['application/pdf'] || 180 },
                  { key: 'application/zip', label: '📦 Archives', defaultVal: retentionConfig?.defaults?.['application/zip'] || 30 },
                  { key: 'retention_days', label: '📁 Par défaut', defaultVal: retentionConfig?.defaults?.default || 90 },
                ].map(({ key, label, defaultVal }) => (
                  <div key={key}>
                    <Label className="text-xs">{label}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="3650"
                      placeholder={`${defaultVal}j (défaut)`}
                      value={retentionForm[key] || ''}
                      onChange={(e) => setRetentionForm({ ...retentionForm, [key]: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="mt-1 h-9"
                    />
                    <span className="text-xs text-muted-foreground">Défaut : {defaultVal}j</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Files */}
        {files.length === 0 ? (
          <Card className="mt-8 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FolderOpen className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Aucun fichier</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                Téléversez vos premiers fichiers pour les partager
              </p>
              <Button 
                className="mt-4 gap-2" 
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4" />
                Téléverser des fichiers
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === "grid" ? (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {files.map((file, index) => (
              <Card 
                key={file.id} 
                className="card-hover group"
                data-testid={`file-card-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                      {getFileIconComponent(file.file_type)}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {isPreviewable(file.file_type) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setPreviewFile(file)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleDownload(file)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setDeleteId(file.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="font-medium text-sm truncate" title={file.original_name}>
                      {file.original_name}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <span>{formatFileSize(file.file_size)}</span>
                      <span>•</span>
                      <Badge variant="outline" className="text-xs">
                        {file.storage_type}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {file.uploaded_by_name}
                    </p>
                    {(() => {
                      const info = getExpiryInfo(file.expires_at);
                      if (!info) return null;
                      const isEditing = editingExpiryId === file.id;
                      return (
                        <div className="flex items-center gap-1.5 mt-2">
                          {isEditing ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Input
                                type="number"
                                min="1"
                                max="3650"
                                value={editingExpiryDays}
                                onChange={(e) => setEditingExpiryDays(e.target.value)}
                                placeholder="jours"
                                className="h-7 w-20 text-xs"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSetExpiry(file.id); if (e.key === 'Escape') setEditingExpiryId(null); }}
                              />
                              <button onClick={() => handleSetExpiry(file.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">✓</button>
                              <button onClick={() => setEditingExpiryId(null)} className="text-xs text-red-500 hover:text-red-700">✕</button>
                            </div>
                          ) : (
                            <Badge
                              className="text-xs gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                              style={{ backgroundColor: info.bg, color: info.color, border: `1px solid ${info.color}30` }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canManage) {
                                  setEditingExpiryId(file.id);
                                  setEditingExpiryDays(String(info.days));
                                }
                              }}
                              title={canManage ? 'Cliquez pour modifier' : `Expire dans ${info.label}`}
                            >
                              <Timer className="h-3 w-3" />
                              {info.label}
                            </Badge>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="mt-8">
            <div className="rounded-lg border">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Taille</th>
                    <th>Type</th>
                    <th>Stockage</th>
                    <th>Uploadé par</th>
                    <th>Date</th>
                    <th>Expiration</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, index) => (
                    <tr key={file.id} data-testid={`file-row-${index}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="text-primary">
                            {getFileIconComponent(file.file_type)}
                          </div>
                          <span className="font-medium truncate max-w-xs">
                            {file.original_name}
                          </span>
                        </div>
                      </td>
                      <td>{formatFileSize(file.file_size)}</td>
                      <td>
                        <Badge variant="secondary" className="font-normal">
                          {file.file_type.split('/')[1] || file.file_type}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant="outline">{file.storage_type}</Badge>
                      </td>
                      <td>{file.uploaded_by_name}</td>
                      <td>{formatDate(file.created_at)}</td>
                      <td>
                        {(() => {
                          const info = getExpiryInfo(file.expires_at);
                          if (!info) return <span className="text-xs text-muted-foreground">—</span>;
                          const isEditing = editingExpiryId === file.id;
                          return (
                            <div className="flex items-center gap-1">
                              {isEditing ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="3650"
                                    value={editingExpiryDays}
                                    onChange={(e) => setEditingExpiryDays(e.target.value)}
                                    placeholder="jours"
                                    className="h-7 w-20 text-xs"
                                    autoFocus
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSetExpiry(file.id); if (e.key === 'Escape') setEditingExpiryId(null); }}
                                  />
                                  <button onClick={() => handleSetExpiry(file.id)} className="text-xs text-green-600 hover:text-green-800 font-medium">✓</button>
                                  <button onClick={() => setEditingExpiryId(null)} className="text-xs text-red-500 hover:text-red-700">✕</button>
                                </div>
                              ) : (
                                <Badge
                                  className="text-xs cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: info.bg, color: info.color, border: `1px solid ${info.color}30` }}
                                  onClick={() => {
                                    if (canManage) {
                                      setEditingExpiryId(file.id);
                                      setEditingExpiryDays(String(info.days));
                                    }
                                  }}
                                  title={canManage ? 'Cliquez pour modifier' : `Expire dans ${info.label}`}
                                >
                                  <Timer className="h-3 w-3 mr-1" />{info.label}
                                </Badge>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {isPreviewable(file.file_type) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setPreviewFile(file)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleDownload(file)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {canManage && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setDeleteId(file.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce fichier ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Preview Modal */}
        {previewFile && (
          <div 
            className="file-preview-overlay"
            onClick={() => setPreviewFile(null)}
          >
            <div className="max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              {previewFile.file_type.startsWith('image/') ? (
                <img 
                  src={getFileUrl(previewFile)}
                  alt={previewFile.original_name}
                  className="max-w-full rounded-lg"
                />
              ) : previewFile.file_type === 'application/pdf' ? (
                <iframe
                  src={getFileUrl(previewFile)}
                  title={previewFile.original_name}
                  className="w-[800px] h-[600px] rounded-lg"
                />
              ) : null}
            </div>
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
